/**
 * Converting between a saved Social Post Template and what the composer works
 * with. Shared by the template editor, "Use template", and the composer's own
 * "Save as template", so all three read and write a template the same way.
 */
import type { PickedMedia } from './ZernioMediaPicker';
import type { CoverSelection } from './ZernioCoverPicker';
import type { SocialPostMedia, SocialPostTemplate } from '@/services/marketing.service';

/**
 * A template's media in the shape the shared media picker and composer use.
 *
 * Duration and dimensions are carried across when the template recorded them,
 * because they are what the composer checks against the chosen platforms (a
 * Reel's length, a Story's 9:16). 0 means "not measured" and is left out, so an
 * unknown value is never mistaken for a zero-length video.
 */
export function toPickedMediaList(items: SocialPostMedia[]): PickedMedia[] {
  return items.map((media) => ({
    url: media.url,
    ...(media.previewUrl ? { previewUrl: media.previewUrl } : {}),
    type: media.type,
    name: media.originalFilename || 'media',
    bytes: media.fileSize,
    ...(media.duration > 0 ? { durationSeconds: media.duration } : {}),
    ...(media.width > 0 ? { width: media.width } : {}),
    ...(media.height > 0 ? { height: media.height } : {}),
  }));
}

/**
 * The picker's media back to what the template API stores.
 *
 * Everything the picker adds has already been uploaded to our own public S3 by
 * the time it reaches here, so `upload` is accurate provenance. The picker does
 * not report a MIME type; the API tolerates an empty one because the file was
 * already format-checked at upload.
 */
export function toTemplateMediaList(items: PickedMedia[]): SocialPostMedia[] {
  return items.map((m) => ({
    type: m.type,
    sourceType: 'upload',
    sourceID: 0,
    url: m.url,
    originalFilename: m.name,
    altText: '',
    fileSize: m.bytes || 0,
    mimeType: '',
    duration: m.durationSeconds ? Math.round(m.durationSeconds) : 0,
    width: m.width ? Math.round(m.width) : 0,
    height: m.height ? Math.round(m.height) : 0,
  }));
}

export function coverOf(t: SocialPostTemplate): CoverSelection | null {
  if (t.coverImageUrl) {
    return { imageUrl: t.coverImageUrl, ...(t.coverPreviewUrl ? { previewUrl: t.coverPreviewUrl } : {}) };
  }
  if (t.coverOffsetMs !== null && t.coverOffsetMs !== undefined) return { offsetMs: t.coverOffsetMs };
  return null;
}

/** Where to play the post's first video from: its display link when it has one. */
export function firstVideoSrc(media: PickedMedia[]): string | null {
  const video = media.find((m) => m.type === 'video');
  return video ? video.previewUrl ?? video.url : null;
}
